import {
	Alert,
	AlertDescription,
	Box,
	Heading,
	Stack,
	Switch,
	useToast,
} from "@chakra-ui/react";
import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { VersionControlSettingsMap } from "../../../types";
import FormTwoColumn from "./FormTwoColumn";

/**
 * Deliberately its own independent query/mutation, not wired through this
 * page's shared react-hook-form/`register()` + "Save Changes" flow: the SDK's
 * own consent notice writes the same underlying option directly, so tying
 * this toggle to the bulk settings save would risk one silently overwriting
 * the other, and would delay the toggle taking effect until an unrelated
 * "Save Changes" click.
 */
const UsageTrackingToggle: React.FC = () => {
	const toast = useToast();
	const queryClient = useQueryClient();

	const trackingQuery = useQuery(["tracking"], () =>
		apiFetch<{ enabled: boolean }>({
			path: "/magazine-blocks/v1/tracking",
		})
	);

	const updateTracking = useMutation(
		(enabled: boolean) =>
			apiFetch<{ enabled: boolean }>({
				path: "/magazine-blocks/v1/tracking",
				method: "POST",
				data: { enabled },
			}),
		{
			onSuccess(data) {
				queryClient.setQueryData(["tracking"], data);
			},
			onError(e: Error) {
				toast({
					status: "error",
					isClosable: true,
					title: __(
						"Error updating usage tracking",
						"magazine-blocks"
					),
					description: e.message,
				});
			},
		}
	);

	return (
		<FormTwoColumn
			label={__("Allow Usage Tracking", "magazine-blocks")}
			description={__(
				"Help improve Magazine Blocks by sharing non-sensitive diagnostic data — your site environment, active theme/plugins, and which blocks you use — with our team to guide new features and fix compatibility issues faster.",
				"magazine-blocks"
			)}
			labelAlignStart
		>
			<Stack mt="1">
				<Switch
					isChecked={trackingQuery.data?.enabled ?? false}
					isDisabled={
						trackingQuery.isLoading || updateTracking.isLoading
					}
					onChange={(e) => updateTracking.mutate(e.target.checked)}
					colorScheme="primary"
					_focusVisible={{
						bg: "#8f24d4",
						outline: "none",
					}}
				/>
				<Alert status="info">
					<AlertDescription>
						{__(
							"Tied to your site's URL — never your page content, admin email, or IP address.",
							"magazine-blocks"
						)}
					</AlertDescription>
				</Alert>
			</Stack>
		</FormTwoColumn>
	);
};

type Props = {
	data?: VersionControlSettingsMap;
};

const VersionControl: React.FC<Props> = (props) => {
	const [version, setVersion] = React.useState<{
		label?: string;
		value?: string;
	}>();
	const { register } = useFormContext();
	const versionControlQuery = useQuery(
		["version-control"],
		() =>
			apiFetch<
				Array<{
					label: string;
					value: string;
				}>
			>({
				path: "/magazine-blocks/v1/version-control",
			}),
		{
			onSuccess(data) {
				setVersion(data[0]);
			},
		}
	);

	return (
		<Box>
			<Heading fontWeight="semibold" fontSize="3xl" as="h2" mb="8">
				{__("Testing & Data", "magazine-blocks")}
			</Heading>
			<Stack
				bgColor="#FAFAFC"
				border="1px"
				borderColor="gray.200"
				borderRadius="lg"
				maxWidth="4xl"
				py="4"
				px="5"
			>
				<Stack gap="7">
					{/* <FormTwoColumn
						label={__('Rollback Version', 'magazine-blocks')}
						description={sprintf(
							__(
								'Encountering a problem with Magazine Blocks version %s? Consider reverting to an earlier version that was trouble-free prior to the issue arising.',
								'magazine-blocks',
							),
							localized.version,
						)}
						labelAlignStart
					>
						<Stack mt="1">
							<HStack>
								<Select
									options={versionControlQuery.data}
									isLoading={versionControlQuery.isLoading}
									value={version}
									isSearchable={false}
									onChange={(value) =>
										setVersion({
											label: value?.label,
											value: value?.value,
										})
									}
									styles={{
										...reactSelectStyles,
										container(base) {
											return {
												...base,
												width: 150,
											};
										},
									}}
									theme={reactSelectTheme}
								/>
								{version && (
									<Button
										variant="outline"
										colorScheme="primary"
										size="sm"
										fontWeight="normal"
										borderRadius="base"
									>
										{sprintf(__('Reinstall v%s', 'magazine-blocks'), version.label)}
									</Button>
								)}
							</HStack>
							<Alert status="warning">
								<AlertDescription>
									{__(
										'Before initiating the rollback, make sure to create a backup of your website.',
										'magazine-blocks',
									)}
								</AlertDescription>
							</Alert>
						</Stack>
					</FormTwoColumn> */}
					<FormTwoColumn
						label={__("Become Beta Tester")}
						description={__(
							"Activate the Beta Tester feature to receive notifications whenever a new beta release of Magazine Blocks becomes available.",
							"magazine-blocks"
						)}
						labelAlignStart
					>
						<Stack mt="1">
							<Switch
								{...register("version-control.beta-tester", {
									value: props.data?.["beta-tester"],
								})}
								colorScheme="primary"
								_focusVisible={{
									bg: "#8f24d4",
									outline: "none",
								}}
							/>
							<Alert status="warning">
								<AlertDescription>
									{__(
										"It is not advisable to install a beta version on production websites.",
										"magazine-blocks"
									)}
								</AlertDescription>
							</Alert>
						</Stack>
					</FormTwoColumn>
					<UsageTrackingToggle />
				</Stack>
			</Stack>
		</Box>
	);
};

export default VersionControl;
